import React, { useEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import itLocale from '@fullcalendar/core/locales/it';
import { EventClickArg, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { parseMetaName } from '../../../Helpers';
import { del, get, postJson, putJson } from '../../../hook/request';

type CategoryApi = {
  ID: number;
  meta?: unknown;
  codice?: string;
  ordine?: number;
  abilitato?: 0 | 1;
};

type CategoryOption = {
  id: number;
  label: string;
  ordine: number;
};

type PresenzaApi = {
  ID?: number | string;
  id?: number | string;
  tipo: number | string;
  giorno?: string;
  data?: string;
  ore?: number | string | null;
};

type Utente = {
  ID: number;
  nome: string;
  cognome?: string;
  email?: string;
};

type DatoPresenza = {
  ore_fatte?: number | string | null;
  ore_rimanenti?: number | string | null;
};

type DatiPresenze = {
  Ferie?: DatoPresenza;
  Permessi?: DatoPresenza;
  '104'?: DatoPresenza;
};

function extractArray(value: any): any[] {
  const root = value?.data ?? value;
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.data)) return root.data;
  return [];
}

function todayValue() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function presenzaStyle(tipo: number) {
  switch (tipo) {
      case 1:
          return { title: 'Festività', color: '#0d6efd', textColor: '#ffffff' };
      case 702:
          return { title: 'Presente', color: '#1d9c61' };

      case 703:
          return { title: 'Ferie', color: '#dc3545' };

      case 704:
          return { title: 'Ore', color: '#ffc107', textColor: '#212529' };

      case 705:
          return { title: 'Malattia', color: '#fd7e14', textColor: '#ffffff' };

      case 706:
          return { title: 'Festività', color: '#0d6efd', textColor: '#ffffff' };

      case 707:
          return { title: '104', color: '#ae00da', textColor: '#ffffff' };

      default:
          return { title: 'Presenza', color: '#6c757d' };
  }
}

export default function PresenzeCalendar() {
  const isAmministratore =
    localStorage.getItem('tipo')?.trim().toLowerCase() === 'amministratore';
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingPresenze, setLoadingPresenze] = useState(true);
  const [loadingDatiPresenze, setLoadingDatiPresenze] = useState(true);
  const [events, setEvents] = useState<EventInput[]>([]);
  const [datiPresenze, setDatiPresenze] = useState<DatiPresenze>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [tipo, setTipo] = useState('');
  const [data, setData] = useState(todayValue());
  const [ore, setOre] = useState('');
  const [userQuery, setUserQuery] = useState('');
  const [userSuggestions, setUserSuggestions] = useState<Utente[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Utente | null>(null);

  const showOre =
    categories.find(category => category.id === Number(tipo))?.ordine === 100;

  const handleUserQueryChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setUserQuery(value);

    if (value.length < 2) {
      setUserSuggestions([]);
      return;
    }

    try {
      setSearchingUsers(true);
      const response = await get<Utente[] | { data?: Utente[] }>(
        `/users/search?temp=${encodeURIComponent(value)}`
      );
      setUserSuggestions(extractArray(response));
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ??
          error?.message ??
          'Errore nella ricerca utenti'
      );
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleSelectUser = (user: Utente) => {
    setSelectedUser(user);
    setUserQuery('');
    setUserSuggestions([]);
  };

  const loadPresenze = async () => {
    try {
      setLoadingPresenze(true);
      const userParam =
        isAmministratore && selectedUser
          ? `?userID=${encodeURIComponent(String(selectedUser.ID))}`
          : '';
      const response = await get<PresenzaApi[] | { data?: PresenzaApi[] }>(
        `/users/presenze${userParam}`
      );

      const mappedEvents: EventInput[] = extractArray(response)
        .map((presenza: PresenzaApi, index: number) => {
          const tipoPresenza = Number(presenza.tipo);
          const rawDate = String(presenza.giorno ?? presenza.data ?? '');
          const day = rawDate.split('T')[0].split(' ')[0];
          if (!day) return null;

          const style = presenzaStyle(tipoPresenza);
          const categoryLabel = categories.find(
            category => category.id === tipoPresenza
          )?.label;
          const presenzaId = presenza.ID ?? presenza.id;

          return {
            id: String(presenzaId ?? `presenza-${index}`),
            title:
              tipoPresenza === 704 && categoryLabel
                ? categoryLabel
                : style.title,
            start: day,
            allDay: true,
            backgroundColor: style.color,
            borderColor: style.color,
            textColor: style.textColor,
            extendedProps: {
              presenzaId,
              tipo: tipoPresenza,
              giorno: day,
              ore: presenza.ore,
            },
          } as EventInput;
        })
        .filter((event: EventInput | null): event is EventInput => event !== null);

      setEvents(mappedEvents);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ??
          error?.message ??
          'Errore nel caricamento delle presenze'
      );
    } finally {
      setLoadingPresenze(false);
    }
  };

  const loadDatiPresenze = async () => {
    try {
      setLoadingDatiPresenze(true);
      const userParam =
        isAmministratore && selectedUser
          ? `?userID=${encodeURIComponent(String(selectedUser.ID))}`
          : '';
      const response = await get<DatiPresenze | { data?: DatiPresenze }>(
        `/users/datiPresenze${userParam}`
      );
      const root: any = (response as any)?.data ?? response;
      setDatiPresenze(root?.data ?? root ?? {});
    } catch (error: any) {
      setDatiPresenze({});
      toast.error(
        error?.response?.data?.message ??
          error?.message ??
          'Errore nel caricamento del riepilogo presenze'
      );
    } finally {
      setLoadingDatiPresenze(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const response = await get<CategoryApi[] | { data?: CategoryApi[] }>(
          '/categories/parent?ID=701'
        );

        const options = extractArray(response)
          .filter(category => (category.abilitato ?? 1) === 1)
          .sort(
            (a, b) => Number(a.ordine ?? 0) - Number(b.ordine ?? 0)
          )
          .map(category => ({
            id: Number(category.ID),
            ordine: Number(category.ordine ?? 0),
            label:
              parseMetaName(category.meta, 'it') ||
              category.codice ||
              String(category.ID),
          }));

        setCategories(options);
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ??
            error?.message ??
            'Errore nel caricamento dei tipi presenza'
        );
      } finally {
        setLoadingCategories(false);
      }
    })();
  }, []);

  useEffect(() => {
    loadPresenze();
    loadDatiPresenze();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, selectedUser?.ID]);

  const openPresenceModal = (date?: string) => {
    setEditingId(null);
    setTipo('');
    setData(date || todayValue());
    setOre('');
    setModalOpen(true);
  };

  const handleDateClick = (info: DateClickArg) => {
    openPresenceModal(info.dateStr);
  };

  const handleEventClick = (info: EventClickArg) => {
    const { presenzaId, tipo: eventTipo, giorno, ore: eventOre } =
      info.event.extendedProps as {
        presenzaId?: number | string;
        tipo: number;
        giorno: string;
        ore?: number | string | null;
      };

    if (presenzaId === undefined || presenzaId === null) {
      toast.error('ID della presenza non disponibile');
      return;
    }

    setEditingId(presenzaId);
    setTipo(String(eventTipo));
    setData(giorno);
    setOre(eventOre === null || eventOre === undefined ? '' : String(eventOre));
    setModalOpen(true);
  };

  const handleTipoChange = (value: string) => {
    setTipo(value);
    const category = categories.find(
      item => item.id === Number(value)
    );
    if (category?.ordine !== 100) setOre('');
  };

  const handleSubmit = async () => {
    if (!tipo || !data) {
      toast.warn('Seleziona il tipo e la data');
      return;
    }

    if (showOre && (!ore || Number(ore) <= 0)) {
      toast.warn('Inserisci le ore');
      return;
    }

    const payload = {
      tipo: Number(tipo),
      data,
      ...(showOre ? { ore: Number(ore) } : {}),
      ...(isAmministratore && selectedUser
        ? { userID: selectedUser.ID }
        : {}),
    };

    try {
      setSaving(true);
      if (editingId !== null) {
        await putJson('/users/presenza', {
          id: editingId,
          ...payload,
        });
        toast.success('Presenza aggiornata con successo');
      } else {
        await postJson('/users/presenza', payload);
        toast.success('Presenza inviata con successo');
      }
      await loadPresenze();
      await loadDatiPresenze();
      setModalOpen(false);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ??
          error?.message ??
          'Errore durante l’invio della presenza'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (editingId === null) return;
    if (!window.confirm('Eliminare questa presenza?')) return;

    try {
      setDeleting(true);
      await del(
        `/users/presenza?ID=${encodeURIComponent(String(editingId))}`
      );
      await loadPresenze();
      await loadDatiPresenze();
      setModalOpen(false);
      toast.success('Presenza eliminata con successo');
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ??
          error?.message ??
          'Errore durante l’eliminazione della presenza'
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="page-body">
        <Row>
          <Col xl={12} className="calendar-basic px-0">
            <Card>
              <CardBody>
                {isAmministratore && (
                  <FormGroup className="position-relative">
                    <Label for="presenze-utente">Utente</Label>
                    {selectedUser ? (
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-primary">
                          {selectedUser.nome} {selectedUser.cognome ?? ''}
                          {selectedUser.email
                            ? ` (${selectedUser.email})`
                            : ''}
                        </span>
                        <Button
                          type="button"
                          color="link"
                          className="p-0"
                          onClick={() => setSelectedUser(null)}
                        >
                          Rimuovi
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Input
                          id="presenze-utente"
                          type="text"
                          placeholder="Cerca utente..."
                          value={userQuery}
                          onChange={handleUserQueryChange}
                          autoComplete="off"
                        />
                        {searchingUsers && (
                          <div className="mt-1">
                            <Spinner size="sm" />
                          </div>
                        )}
                        {userSuggestions.length > 0 && (
                          <ListGroup
                            className="position-absolute w-100 mt-1"
                            style={{ zIndex: 1000 }}
                          >
                            {userSuggestions.map(user => (
                              <ListGroupItem
                                key={user.ID}
                                action
                                onClick={() => handleSelectUser(user)}
                              >
                                {user.nome} {user.cognome ?? ''}
                                {user.email && (
                                  <span className="text-muted">
                                    {' '}
                                    ({user.email})
                                  </span>
                                )}
                              </ListGroupItem>
                            ))}
                          </ListGroup>
                        )}
                      </>
                    )}
                  </FormGroup>
                )}

                {(loadingCategories ||
                  loadingPresenze ||
                  loadingDatiPresenze) && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <Spinner size="sm" />
                    <span>Caricamento presenze...</span>
                  </div>
                )}

                <Row>
                  <Col xl={2}>
                    <div className="md-sidebar mb-3 box-col-12">
                      <div className="md-sidebar-aside job-left-aside custom-scrollbar">
                        <h4>RIEPILOGO</h4>
                        {(['Ferie', 'Permessi', '104'] as const).map(
                          voce => {
                            const dato = datiPresenze[voce];
                            if (!dato) return null;

                            return (
                              <div
                                key={voce}
                                className="border rounded p-3 mt-3"
                              >
                                <h6 className="mb-2">{voce}</h6>
                                <div className="small">
                                  Giorni fatti:{' '}
                                  <strong>{dato.ore_fatte ?? 0}</strong>
                                </div>
                                <div className="small">
                                   Giorni rimanenti:{' '}
                                  <strong>{dato.ore_rimanenti ?? 0}</strong>
                                </div>
                              </div>
                            );
                          }
                        )}

                        {!loadingDatiPresenze &&
                          Object.keys(datiPresenze).length === 0 && (
                            <p className="text-muted small mt-3">
                              Nessun dato disponibile
                            </p>
                          )}
                      </div>
                    </div>
                  </Col>

                  <Col xl={10}>
                    <div className="calendar-default presenze-calendar">
                      <FullCalendar
                        locale={itLocale}
                        initialView="dayGridMonth"
                        buttonText={{
                          today: 'Oggi',
                          month: 'Mese',
                          week: 'Settimana',
                          day: 'Giorno',
                        }}
                        headerToolbar={{
                          left: 'prev,next today',
                          center: 'title',
                          right: 'dayGridMonth,timeGridWeek,timeGridDay',
                        }}
                        height="85vh"
                        plugins={[
                          dayGridPlugin,
                          timeGridPlugin,
                          interactionPlugin,
                        ]}
                        dateClick={handleDateClick}
                        eventClick={handleEventClick}
                        events={events}
                      />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          {editingId !== null ? 'Modifica presenza' : 'Nuova presenza'}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="presenza-tipo">Tipo</Label>
            <Input
              id="presenza-tipo"
              type="select"
              value={tipo}
              onChange={event => handleTipoChange(event.target.value)}
              disabled={loadingCategories}
            >
              <option value="">
                {loadingCategories ? 'Caricamento...' : 'Seleziona...'}
              </option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.label}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup>
            <Label for="presenza-data">Data</Label>
            <Input
              id="presenza-data"
              type="date"
              value={data}
              onChange={event => setData(event.target.value)}
            />
          </FormGroup>

          {showOre && (
            <FormGroup>
              <Label for="presenza-ore">Ore</Label>
              <Input
                id="presenza-ore"
                type="number"
                min={0}
                step="0.25"
                value={ore}
                onChange={event => setOre(event.target.value)}
                placeholder="Inserisci le ore"
              />
            </FormGroup>
          )}
        </ModalBody>
        <ModalFooter>

        <div className='d-flex justify-content-between align-items-center w-100'>
          
          <div>
            {editingId !== null && (
            <Button
              color="danger"
              onClick={handleDelete}
              disabled={saving || deleting}
            >
              {deleting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Eliminazione...
                </>
              ) : (
                'Elimina'
              )}
            </Button>
          )}
          </div>

          <div>
            <Button
            color="secondary"
            onClick={() => setModalOpen(false)}
            disabled={saving || deleting}
          >
            Chiudi
          </Button>
         
          <Button
            color="primary"
            onClick={handleSubmit}
            className='ms-3'
            disabled={saving || deleting}
          >
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" />
                Invio...
              </>
            ) : (
              editingId !== null ? 'Aggiorna' : 'Invia'
            )}
          </Button>
          </div>

        </div>

          
        </ModalFooter>
      </Modal>
    </>
  );
}
